<template>
  <header>
    <a-form class="form" layout="vertical" ref="formRef" :model="formState" :rules="rules">
      <a-form-item v-if="user?.customer?.type_business == 1" label="Empresa" name="entity" required>
        <entity-picker
          :showAll="false"
          defaultBehavior
          :value="formState.entity"
          @update:value="formState.entity = $event"
        />
      </a-form-item>

      <a-form-item label="Campaña" name="portfolio" required>
        <campaign-picker
          :showAll="false"
          :value="formState.portfolio"
          @update:value="formState.portfolio = $event"
          :entity="formState.entity"
        />
      </a-form-item>

      <a-form-item v-if="user?.customer?.enable_bases" label="Base" name="base" required>
        <bases-picker
          defaultBehavior
          :value="formState.base"
          @update:value="formState.base = $event"
          :entity="formState.entity"
          :portfolio="formState.portfolio"
        />
      </a-form-item>

      <PlanSeparatedDatePicker
        v-model:startDate="formState.startDate"
        v-model:endDate="formState.endDate"
        startPlaceholder="-- / -- / ----"
        endPlaceholder="-- / -- / ----"
      />
    </a-form>

    <div class="actions">
      <span v-if="totalRecords !== null" class="total">{{ totalRecords }} Registros</span>

      <a-tooltip placement="top">
        <template #title>
          <span v-if="isGlobalDownloading"> Tiene una descarga pendiente </span>
          <span v-else>
            Se descargarán únicamente los datos de la tabla de acuerdo a los filtros seleccionados.
          </span>
        </template>

        <a-button
          type="link"
          :disabled="isButtonDisabled || isGlobalDownloading"
          :loading="isDownloading"
          @click="handleDownload"
        >
          Descargar
          <DownloadOutlined />
        </a-button>
      </a-tooltip>

      <a-button type="primary" :disabled="isButtonDisabled || loadingSearch" :loading="loadingSearch" @click="handleSearch">
        Buscar
        <SearchOutlined />
      </a-button>
    </div>
  </header>

  <a-table
    size="middle"
    id="inbound-table"
    :scroll="scrollStyle"
    :columns="inboundVoiceColumns"
    :data-source="reportData?.results"
    :loading="isTableLoading"
    :pagination="pagination"
    @change="handleTableChange"
  >
    <template #percentCell="{ text }"> {{ formatToPercent(text) }} </template>

    <template #summary>
      <a-table-summary fixed>
        <a-table-summary-row>
          <a-table-summary-cell>Total</a-table-summary-cell>

          <a-table-summary-cell></a-table-summary-cell>

          <a-table-summary-cell></a-table-summary-cell>

          <a-table-summary-cell> {{ reportData?.total?.contestadas }} </a-table-summary-cell>

          <a-table-summary-cell> {{ reportData?.total?.no_contestadas }} </a-table-summary-cell>

          <a-table-summary-cell> {{ reportData?.total?.total }} </a-table-summary-cell>

          <a-table-summary-cell> {{ reportData?.total?.total_seconds }} </a-table-summary-cell>

          <a-table-summary-cell> {{ reportData?.total?.atendidas_ns }} </a-table-summary-cell>

          <a-table-summary-cell>
            {{ formatToPercent(reportData?.total?.nivel_servicio) }}
          </a-table-summary-cell>

          <a-table-summary-cell>
            {{ formatToPercent(reportData?.total?.nivel_abandono) }}
          </a-table-summary-cell>

          <!-- <a-table-summary-cell> {{ reportData?.total?.umbral_espera }} </a-table-summary-cell> -->
        </a-table-summary-row>
      </a-table-summary>
    </template>
  </a-table>
</template>

<script setup>
import { computed, reactive, ref } from "vue";
import { useStore } from "vuex";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons-vue";

import { downloadInboundVoiceReport, fetchInboundVoice } from "@/app/reports/services";
import { getInboundVoiceTotals } from "@/app/reports/utils/helpers";
import { inboundVoiceColumns } from "@/app/reports/utils/tableColumns/inboundVoice";
import BasesPicker from "@/app/shared/components/pickers/BasesPicker.vue";
import CampaignPicker from "@/app/shared/components/pickers/CampaignPicker.vue";
import EntityPicker from "@/app/shared/components/pickers/EntityPicker.vue";
import { useGlobalDownloadState } from "@/app/shared/composables/useGlobalDownloadState";
import { useScrollResize } from "@/app/shared/composables/useScrollResize";
import { TABLE_PAGE_SIZE } from "@/app/shared/utils/constants";

import PlanSeparatedDatePicker from "@/app/shared/components/pickers/PlanSeparatedDatePicker.vue";

const store = useStore();

const user = computed(() => store.getters["getDataUser"]);
const { scrollStyle } = useScrollResize({ headerMargin: 465 });

// Form
const formState = reactive({
  portfolio: null,
  startDate: null,
  endDate: null,
});
const totalRecords = ref(null);
const isButtonDisabled = computed(() => {
  for (const key in formState) {
    if (formState[key] === null || formState[key] === undefined) return true;
  }

  return false;
});

const { isGlobalDownloading } = useGlobalDownloadState();
const isDownloading = ref(false);

const formatToPercent = (value) => {
  if (typeof value === "undefined") return "";

  return `${(value * 100).toFixed(0)}%`;
};

// Table
const reportData = ref(null);
const currentTablePage = ref(1);
const isTableLoading = ref(false);
const loadingSearch = ref(false);

const pagination = computed(() => ({
  total: reportData.value?.count,
  current: currentTablePage.value,
  pageSize: TABLE_PAGE_SIZE,
  showSizeChanger: false,
}));

const handleTableChange = async (page) => {
  currentTablePage.value = page.current;
  await fetchReportData();
};

const fetchReportData = async () => {
  isTableLoading.value = true;

  const response = await fetchInboundVoice({ filters: formState, page: currentTablePage.value });
  reportData.value = getInboundVoiceTotals(response);
  totalRecords.value = reportData.value?.count;

  isTableLoading.value = false;
};

const handleSearch = async () => {
  if (loadingSearch.value) return;

  try {
    loadingSearch.value = true;
    currentTablePage.value = 1;
    await fetchReportData();
  } catch (error) {
    console.log("Error handleSearch InboundVoice", error);
  } finally {
    loadingSearch.value = false;
  }
};

const handleDownload = async () => {
  isDownloading.value = true;
  await downloadInboundVoiceReport({ filters: formState });
  isDownloading.value = false;
};
</script>

<style lang="sass" scoped>
header
  display: flex
  justify-content: space-between
  align-items: flex-end
  margin: 15px 0

.form
  display: flex
  gap: 10px

.ant-form-item
  margin: 0
  width: 180px

:deep(.ant-form-item-label)
  padding: 0

.actions
  display: flex
  align-items: center
  gap: 10px

.date-picker
  width: 100%

#inbound-table tfoot th,
#inbound-table tfoot td
  background-color: #fafafa !important
</style>
